import { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Banner,
  formatPaise,
  SHOW_CREDIT_KHATA,
  QRCodeView,
  calculateEffectiveTierPrice,
  MDD_SUPPORT_PHONE_DISPLAY,
  MDD_SUPPORT_WHATSAPP_URL,
  resolvePack,
  packView,
  cartLineKey,
} from "@mydailydirect/ui";
import {
  X,
  Minus,
  Plus,
  Trash2,
  Banknote,
  QrCode,
  CreditCard,
  UserCheck,
  CheckCircle2,
  Smartphone,
  Camera,
  Store,
  MessageSquare,
  ShoppingBag,
  Sparkles,
  Layers,
} from "lucide-react";
import { useCart } from "../lib/cart";
import { useRetailerId } from "../lib/useRetailerId";
import { useQuery } from "@tanstack/react-query";
import { useMyDailyDirectClient, track } from "@mydailydirect/app-kit";
import { UploadShopProofModal } from "./UploadShopProofModal";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  cartTotal: number;
  onPlaceOrder: (paymentMethod: string) => void;
  isPlacingOrder: boolean;
}

export function CartDrawer({
  isOpen,
  onClose,
  cartTotal,
  onPlaceOrder,
  isPlacingOrder,
}: CartDrawerProps) {
  const cart = useCart();
  const client = useMyDailyDirectClient();
  const { retailer, buyerName, buyerPhone, isVerified, isApproved, hasShopProof, hasGstNumber } = useRetailerId();
  const [paymentMethod, setPaymentMethod] = useState<"COD" | "UPI" | "CREDIT">("COD");

  const [showProofModal, setShowProofModal] = useState(false);
  const skusQuery = useQuery({ queryKey: ["skus"], queryFn: () => client.skus.list() });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      if (cart.lines.length > 0) {
        track("begin_checkout", {
          valuePaise: cartTotal,
          retailerId: retailer?.id ?? undefined,
          propsJson: {
            itemCount: cart.lines.length,
          },
        });
      }
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const totalUnits = cart.lines.reduce((sum, line) => sum + line.qty, 0);

  const bulkSavingsTotal = useMemo(() => {
    if (!skusQuery.data) return 0;
    const byId = new Map(skusQuery.data.map((s) => [s.id, s]));
    let totalSavings = 0;
    cart.lines.forEach((l) => {
      const sku = byId.get(l.skuId);
      if (sku) {
        const pack = resolvePack(sku, l.packId);
        const basePrice = packView(sku, pack).pricePaise;
        const tierCalc = calculateEffectiveTierPrice(sku, l.qty);
        if (tierCalc.discountPercent > 0) {
          totalSavings += Math.round(basePrice * l.qty * (tierCalc.discountPercent / 100));
        }
      }
    });
    return totalSavings;
  }, [cart.lines, skusQuery.data]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] flex justify-end transition-opacity duration-200 ${
        isOpen ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
      }`}
      style={{ zIndex: 9999 }}
    >
      {/* Dimmed Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-xs transition-opacity duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Spring Slide-In Drawer Container - 100vw on mobile, max-w-md on desktop */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-drawer-title"
        className={`relative w-full sm:max-w-md md:max-w-lg h-full bg-background border-l border-border flex flex-col z-10 shadow-overlay transition-transform duration-300 ease-out transform ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-border bg-card ds-chrome">
          <div className="min-w-0">
            <h2 id="cart-drawer-title" className="text-base sm:text-lg font-black text-foreground flex items-center gap-2 type-title">
              <ShoppingBag className="w-5 h-5 text-cta shrink-0" />
              <span>Your Wholesale Cart</span>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {cart.lines.length} {cart.lines.length === 1 ? "item" : "items"} · {totalUnits} total units
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-10 w-10 min-h-[44px] min-w-[44px] rounded-control hover:bg-muted shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Close Cart Drawer"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Drawer Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-3.5 sm:p-5 space-y-4">
          {cart.pushedBy && (
            <Banner
              tone="warning"
              layout="card"
              title={`Added to your cart by ${cart.pushedBy}`}
              icon={UserCheck}
            >
              Your Relationship Manager pre-loaded this order for your review.
            </Banner>
          )}

          {cart.lines.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground space-y-3">
              <div className="w-16 h-16 mx-auto rounded-full bg-muted/60 flex items-center justify-center text-3xl">
                🛒
              </div>
              <div className="font-bold text-foreground text-base">Your cart is empty</div>
              <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                Browse our catalog and add bulk items to calculate prices and schedule your morning delivery.
              </p>
              <Button
                variant="primary"
                size="sm"
                onClick={onClose}
                className="mt-2 font-bold bg-cta text-cta-foreground hover:opacity-95"
              >
                Browse Catalog
              </Button>
            </div>
          ) : (
            <>
              {/* Order Items List */}
              <div className="space-y-3">
                <div className="flex items-center justify-between text-2xs font-extrabold uppercase tracking-wider text-muted-foreground">
                  <span>Order Items ({cart.lines.length})</span>
                  <span>Wholesale Rate</span>
                </div>

                {cart.lines.map((line) => {
                  const sku = skusQuery.data?.find((s) => s.id === line.skuId);
                  if (!sku) return null;

                  const pack = resolvePack(sku, line.packId);
                  const view = packView(sku, pack);
                  const unitPrice = view.pricePaise;
                  const tierCalc = calculateEffectiveTierPrice(sku, line.qty);
                  const discount = tierCalc.discountPercent / 100;
                  const effectiveUnitPrice = Math.round(unitPrice * (1 - discount));
                  const lineTotal = effectiveUnitPrice * line.qty;
                  const lineSavings = (unitPrice - effectiveUnitPrice) * line.qty;
                  const maxAvailable = view.availableQty;
                  const lineKey = cartLineKey(line.skuId, line.packId);

                  return (
                    <div
                      key={lineKey}
                      className="flex flex-col sm:flex-row gap-3 p-3.5 rounded-card border border-border bg-card shadow-2xs hover:border-border/80 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-bold text-foreground truncate">{sku.name}</span>
                          {view.label && (
                            <span className="inline-flex items-center gap-1 rounded bg-primary/10 border border-primary/20 px-1.5 py-0.5 text-[10px] font-bold text-primary shrink-0">
                              <Layers className="h-2.5 w-2.5" /> {view.label}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mb-2.5">
                          {formatPaise(unitPrice)} {view.label ? `per ${view.label}` : (sku.unit ? `/ ${sku.unit}` : "")}
                        </div>

                        {/* Finger-Friendly Touch Target Stepper (Min 44px) */}
                        <div className="flex items-center gap-2">
                          <div className="flex items-center rounded-control border border-border bg-sunken p-0.5">
                            <button
                              type="button"
                              onClick={() => cart.setQty(line.skuId, line.qty - 1, line.packId)}
                              className="h-10 w-10 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-foreground hover:bg-muted font-bold active:scale-95 transition-all"
                              aria-label="Decrease quantity"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="w-9 text-center text-sm font-extrabold text-foreground type-num select-none">
                              {line.qty}
                            </span>
                            <button
                              type="button"
                              disabled={line.qty >= maxAvailable}
                              onClick={() => cart.setQty(line.skuId, line.qty + 1, line.packId)}
                              className="h-10 w-10 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-foreground hover:bg-muted font-bold active:scale-95 disabled:opacity-40 transition-all"
                              aria-label="Increase quantity"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>

                          <button
                              type="button"
                              onClick={() => cart.setQty(line.skuId, 0, line.packId)}
                              className="h-10 w-10 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-control text-muted-foreground hover:text-destructive hover:bg-tone-danger-soft transition-all ml-auto sm:ml-2"
                              aria-label="Remove item"
                              title="Remove from cart"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                        </div>
                      </div>

                      <div className="text-right shrink-0 flex sm:flex-col justify-between sm:justify-center border-t sm:border-t-0 pt-2 sm:pt-0 border-border/50">
                        <span className="text-xs text-muted-foreground sm:hidden font-medium">Item Total</span>
                        <div>
                          <div className="font-extrabold text-sm sm:text-base text-foreground type-num">
                            {formatPaise(lineTotal)}
                          </div>
                          {lineSavings > 0 && (
                            <div className="text-2xs text-tone-success-text font-bold mt-0.5">
                              Save {formatPaise(lineSavings)} ({tierCalc.discountPercent}% QPS)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Payment Method Segmented Control */}
              <div className="space-y-3 pt-3 border-t border-border">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xs font-extrabold uppercase tracking-wider text-muted-foreground">
                    Payment Method
                  </h3>
                  <span className="text-2xs text-muted-foreground">Direct Wholesale Settled</span>
                </div>

                {/* Segmented Control Buttons */}
                <div className="grid grid-cols-3 p-1 rounded-card bg-sunken border border-border gap-1">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod("COD")}
                    className={`py-2 px-1 text-xs font-bold rounded-control transition-all flex flex-col sm:flex-row items-center justify-center gap-1.5 min-h-[44px] ${
                      paymentMethod === "COD"
                        ? "bg-card text-foreground shadow-xs ring-1 ring-border"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <Banknote className="h-4 w-4 shrink-0 text-cta" />
                    <span>Pay on Delivery</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod("UPI")}
                    className={`py-2 px-1 text-xs font-bold rounded-control transition-all flex flex-col sm:flex-row items-center justify-center gap-1.5 min-h-[44px] ${
                      paymentMethod === "UPI"
                        ? "bg-card text-foreground shadow-xs ring-1 ring-border"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <QrCode className="h-4 w-4 shrink-0 text-cta" />
                    <span>Instant UPI</span>
                  </button>

                  {SHOW_CREDIT_KHATA && (
                    <button
                      type="button"
                      onClick={() => setPaymentMethod("CREDIT")}
                      className={`py-2 px-1 text-xs font-bold rounded-control transition-all flex flex-col sm:flex-row items-center justify-center gap-1.5 min-h-[44px] ${
                        paymentMethod === "CREDIT"
                          ? "bg-card text-foreground shadow-xs ring-1 ring-border"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <CreditCard className="h-4 w-4 shrink-0 text-cta" />
                      <span>Net 30 Credit</span>
                    </button>
                  )}
                </div>

                {/* Selected Payment Option Details & UPI QR Mockup */}
                {paymentMethod === "COD" && (
                  <div className="p-3.5 rounded-card border border-border bg-card space-y-1 text-xs">
                    <div className="font-bold text-foreground flex items-center gap-2">
                      <Banknote className="h-4 w-4 text-tone-success-text shrink-0" />
                      <span>Cash or UPI on Driver Arrival</span>
                    </div>
                    <p className="text-muted-foreground text-xs leading-relaxed">
                      Pay by cash or scan driver's QR when your goods arrive at your shop entrance in the morning slot.
                    </p>
                  </div>
                )}

                {paymentMethod === "UPI" && (
                  <div className="p-4 rounded-card border border-tone-brand-border bg-tone-brand-soft space-y-3">
                    <div className="flex items-center justify-between border-b border-tone-brand-border/40 pb-2">
                      <div className="flex items-center gap-2 font-black text-xs sm:text-sm text-foreground">
                        <QrCode className="h-4 w-4 text-cta" />
                        <span>Instant UPI QR Settlement</span>
                      </div>
                      <span className="text-2xs font-black uppercase text-tone-brand-text bg-card px-2 py-0.5 rounded-full border border-tone-brand-border">
                        Zero Fee
                      </span>
                    </div>

                    {/* Real Scannable UPI QR Code */}
                    <div className="flex flex-col items-center justify-center p-4 bg-white rounded-card border border-border text-center space-y-2 shadow-2xs">
                      <div className="p-2.5 bg-white rounded-control border-2 border-slate-900">
                        <QRCodeView
                          value={`upi://pay?pa=mdd.wholesales@icici&pn=My+Daily+Direct&am=${(cartTotal / 100).toFixed(2)}&cu=INR`}
                          size={135}
                          alt="Instant UPI QR Code"
                        />
                      </div>
                      <div className="text-xs font-bold text-slate-800">
                        VPA: <span className="text-slate-950 font-mono font-black">mdd.wholesales@icici</span>
                      </div>
                      <div className="text-xs font-semibold text-slate-600">
                        Amount: <span className="font-black text-slate-950">{formatPaise(cartTotal)}</span>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between text-[11px] text-muted-foreground gap-1.5 pt-1">
                      <span className="flex items-center gap-1 font-medium">
                        <Smartphone className="h-3.5 w-3.5 text-cta shrink-0" /> PhonePe, GPay, Paytm, BHIM
                      </span>
                      <span className="flex items-center gap-1 text-tone-success-text font-bold">
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" /> Instant Confirmation
                      </span>
                    </div>
                  </div>
                )}

                {SHOW_CREDIT_KHATA && paymentMethod === "CREDIT" && (
                  <div className="p-3.5 rounded-card border border-tone-success-border bg-tone-success-soft space-y-1 text-xs">
                    <div className="font-bold text-tone-success-text flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-tone-success-text shrink-0" />
                        <span>Net 30 Day Trade Khata</span>
                      </span>
                      <span className="text-2xs font-black uppercase bg-card px-2 py-0.5 rounded-full border border-tone-success-border">
                        Approved
                      </span>
                    </div>
                    <p className="text-tone-success-text/90 leading-relaxed text-xs">
                      Billed to your registered trade account. Due within 30 days ({formatPaise(cartTotal)}).
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Drawer Footer with Safe Area */}
        {cart.lines.length > 0 && (
          <div className="p-4 sm:p-5 border-t border-border bg-card space-y-3 shadow-overlay pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
            <div className="space-y-1.5">
              <div className="flex justify-between items-center text-xs text-muted-foreground">
                <span>Subtotal ({cart.lines.length} items)</span>
                <span className="font-semibold text-foreground type-num">{formatPaise(cartTotal + bulkSavingsTotal)}</span>
              </div>
              {bulkSavingsTotal > 0 && (
                <div className="flex justify-between items-center text-xs text-tone-success-text font-bold bg-tone-success-soft px-3 py-1.5 rounded-control border border-tone-success-border">
                  <span className="flex items-center gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>QPS Volume Slab Discount</span>
                  </span>
                  <span className="type-num">- {formatPaise(bulkSavingsTotal)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-border text-sm">
                <span className="font-bold text-foreground">Total Payable</span>
                <span className="text-xl sm:text-2xl font-black text-foreground type-num">{formatPaise(cartTotal)}</span>
              </div>
            </div>

            {/* Shop KYC & Admin Verification Gate Notices */}
            {(!hasShopProof || !hasGstNumber) && (
              <div className="p-3 rounded-card border border-tone-warning-border bg-tone-warning-soft space-y-2">
                <div className="flex items-start gap-2.5">
                  <Store className="h-4 w-4 text-tone-warning-text shrink-0 mt-0.5" />
                  <div className="space-y-0.5 min-w-0">
                    <p className="text-xs font-bold text-tone-warning-text">
                      GSTIN & Storefront Board Photo Required
                    </p>
                    <p className="text-2xs text-tone-warning-text/80 leading-relaxed">
                      Wholesale ordering requires a valid 15-character GSTIN and photo of your shop board.
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={() => setShowProofModal(true)}
                  className="w-full text-xs font-bold bg-tone-warning-text text-white hover:brightness-110 h-9 gap-1.5 rounded-control"
                >
                  <Camera className="h-4 w-4" /> Upload GSTIN & Shop Proof
                </Button>
              </div>
            )}

            {hasShopProof && hasGstNumber && !isApproved && (
              <div className="p-3 rounded-card border border-tone-info-border bg-tone-info-soft space-y-1 text-xs">
                <div className="font-bold text-tone-info-text flex items-center gap-1.5">
                  <Store className="h-4 w-4 text-tone-info-text shrink-0" />
                  <span>Account Under Manual Admin Review</span>
                </div>
                <p className="text-2xs text-tone-info-text/80 leading-relaxed">
                  Your documents are being verified by our operations team. Checkout unlocks upon approval.
                </p>
              </div>
            )}

            <Button
              className="w-full min-h-[48px] text-sm sm:text-base font-black shadow-md bg-cta text-cta-foreground hover:brightness-105 active:scale-[0.99] rounded-control"
              size="lg"
              loading={isPlacingOrder}
              disabled={isPlacingOrder || !isVerified}
              disabledReason={
                !isVerified
                  ? !hasGstNumber
                    ? "Disabled: Valid 15-character GSTIN required before ordering"
                    : !hasShopProof
                    ? "Disabled: Supporting storefront photo required before ordering"
                    : "Disabled: Retailer account is pending manual admin verification"
                  : undefined
              }
              onClick={() => {
                if (!isVerified) {
                  if (!hasShopProof || !hasGstNumber) {
                    setShowProofModal(true);
                  }
                  return;
                }
                if (isPlacingOrder) return;
                onPlaceOrder(paymentMethod);
              }}
            >
              {isVerified ? (
                `Place Wholesale Order (${
                  paymentMethod === "COD"
                    ? "Pay on Delivery"
                    : paymentMethod === "UPI"
                    ? "Instant UPI QR"
                    : "Net 30 Credit"
                })`
              ) : !hasShopProof || !hasGstNumber ? (
                <span className="flex items-center justify-center gap-2 text-xs sm:text-sm font-bold">
                  <Camera className="h-4 w-4" />
                  Upload GSTIN & Shop Proof to Order
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2 text-xs sm:text-sm font-bold opacity-80">
                  Pending Manual Admin Approval
                </span>
              )}
            </Button>

            {/* 1-Click WhatsApp Order Flow */}
            <Button
              type="button"
              variant="outline"
              size="md"
              disabled={isPlacingOrder || !isVerified}
              disabledReason={
                !isVerified
                  ? !hasGstNumber
                    ? "Disabled: Valid 15-character GSTIN required before ordering"
                    : !hasShopProof
                    ? "Disabled: Supporting storefront photo required before ordering"
                    : "Disabled: Retailer account is pending manual admin verification"
                  : undefined
              }
              onClick={() => {
                if (!isVerified) {
                  if (!hasShopProof || !hasGstNumber) {
                    setShowProofModal(true);
                  }
                  return;
                }

                const skuMap = new Map((skusQuery.data ?? []).map((s) => [s.id, s]));
                const itemLines = cart.lines
                  .map((l) => {
                    const s = skuMap.get(l.skuId);
                    if (!s) return null;
                    const pack = resolvePack(s, l.packId);
                    const view = packView(s, pack);
                    const lineRs = ((view.pricePaise * l.qty) / 100).toFixed(0);
                    const packStr = view.label ? ` (${view.label})` : "";
                    return `• ${s.name}${packStr} x ${l.qty} = ₹${lineRs}`;
                  })
                  .filter(Boolean)
                  .join("\n");

                const gpsLink =
                  retailer?.latitude && retailer?.longitude
                    ? `https://maps.google.com/?q=${retailer.latitude},${retailer.longitude}`
                    : retailer?.addressLine || "Bengaluru";

                const waText = encodeURIComponent(
                  `🌾 *WHOLESALE ORDER - MYDAILYDIRECT*\n` +
                    `🏪 Shop: ${buyerName}\n` +
                    `📞 Phone: ${buyerPhone}\n` +
                    `📍 Location: ${gpsLink}\n\n` +
                    `📦 *Order Items:*\n${itemLines}\n\n` +
                    `💰 *Total Payable: ${formatPaise(cartTotal)}* (${paymentMethod})\n` +
                    `🚚 *Slot:* Morning Delivery (6:00 AM - 9:00 AM)\n` +
                    `📞 *For Help / Support:* Call or WhatsApp ${MDD_SUPPORT_PHONE_DISPLAY}`
                );

                window.open(`${MDD_SUPPORT_WHATSAPP_URL}?text=${waText}`, "_blank", "noopener,noreferrer");
                onPlaceOrder(paymentMethod);
              }}
              className="w-full min-h-[44px] text-xs sm:text-sm font-bold border-tone-success-border text-tone-success-text bg-tone-success-soft hover:brightness-95 gap-2 rounded-control"
            >
              <MessageSquare className="h-4 w-4 text-tone-success-text shrink-0" />
              <span>Order via WhatsApp ({MDD_SUPPORT_PHONE_DISPLAY})</span>
            </Button>
          </div>
        )}
      </div>

      {/* Reusable Shop Proof Upload Modal */}
      <UploadShopProofModal
        open={showProofModal}
        onClose={() => setShowProofModal(false)}
        retailer={retailer}
      />
    </div>,
    document.body
  );
}
